import { Box, CircularProgress, LinearProgress, Stack, Typography, styled } from "@mui/material";
import { MRT_RowData, MRT_TableInstance, MaterialReactTable } from "material-react-table";
import { ReactElement } from "react";

import { IconPlusCircle } from "@/modules/irohana-components/assets/icons/IconPlusCircle";
import { background, button, divider, green, text } from "@/modules/irohana-components/theme/colors";
import { rounded, spacing } from "@/modules/irohana-components/theme/variables";
import { isNumber } from "@/modules/irohana-components/utils/number";

export type TTableExtendOptions = { addNewRow?: () => void };

type TTableProps<T extends MRT_RowData> = {
  addBtnText?: string;
  addSecondBtnText?: string;
  addSecondBtnOnClick?: () => void;
  table: MRT_TableInstance<T> & TTableExtendOptions;
  isLoading?: boolean;
  isFetching?: boolean;
  maxHeight?: number | string;
  headRowHeight?: number | string;
  bodyRowHeight?: number | string;
  addNewRow?: () => void;
  id?: string;
  hiddenAddBtn?: boolean;
  extendedRow?: ReactElement;
  borderColor?: string;
  height?: number | string;
  minHeight?: number | string;
};

export const Table = <T extends MRT_RowData>({
  addBtnText,
  addSecondBtnText,
  addSecondBtnOnClick,
  table,
  isLoading,
  isFetching,
  maxHeight,
  headRowHeight = 40,
  bodyRowHeight = 53,
  id,
  hiddenAddBtn,
  extendedRow,
  borderColor = divider.middle,
  height,
  minHeight,
}: TTableProps<T>) => {
  const { addNewRow } = table;
  const hasFnAddRow = !!addNewRow;
  const hasSecondBtn = !!addSecondBtnOnClick && !!addSecondBtnText;

  return (
    <StyledTable
      id={id}
      tableMaxHeight={maxHeight}
      headRowHeight={headRowHeight}
      bodyRowHeight={bodyRowHeight}
      height={height}
      minHeight={minHeight}
      borderColor={borderColor}
      pb={(hasFnAddRow || hasSecondBtn) && !hiddenAddBtn ? 4.5 : 0}
    >
      <MaterialReactTable table={table} />
      {table.getRowCount() === 0 && (
        <CenterBox>
          <Typography color={text.tertiary} fontWeight={700}>
            データがありません。
          </Typography>
        </CenterBox>
      )}
      {isLoading && (
        <CenterBox>
          <CircularProgress sx={{ color: text.tertiary }} size="24px" />
        </CenterBox>
      )}
      {isFetching && !isLoading && <LinearProgress sx={{ height: 3 }} color="inherit" />}
      {(((hasFnAddRow || hasSecondBtn) && !hiddenAddBtn) || extendedRow) && (
        <Stack
          flexDirection="row"
          sx={{
            position: "absolute",
            marginTop: "8px",
            marginLeft: "16px",
            gap: "16px",
          }}
        >
          {hasFnAddRow && !hiddenAddBtn && (
            <AddRowButton onClick={addNewRow}>
              <IconPlusCircle color={background.main} />
              <Typography variant="cap12" padding="0px !important">
                {addBtnText ?? "行を追加する"}
              </Typography>
            </AddRowButton>
          )}

          {hasSecondBtn && !hiddenAddBtn && (
            <AddRowButton onClick={addSecondBtnOnClick}>
              <IconPlusCircle color={background.main} />
              <Typography variant="cap12" padding="0px !important">
                {addSecondBtnText}
              </Typography>
            </AddRowButton>
          )}

          {extendedRow}
        </Stack>
      )}
    </StyledTable>
  );
};

const options = { shouldForwardProp: (propName: string) => !["tableMaxHeight"].includes(propName) };

const StyledTable = styled(Box, options)<{
  tableMaxHeight?: number | string;
  headRowHeight?: number | string;
  bodyRowHeight?: number | string;
  borderColor?: string;
}>`
  position: relative;
  border-radius: ${rounded.sm};
  overflow: hidden;
  border: 1px solid ${({ borderColor }) => borderColor};
  * {
    box-shadow: none;
  }
  .MuiTableContainer-root {
    max-height: ${({ tableMaxHeight }) => `${tableMaxHeight ? (isNumber(tableMaxHeight) ? `${tableMaxHeight}px` : tableMaxHeight) : "unset"}`};
    height: ${({ height }) => `${height ? (isNumber(height) ? `${height}px` : height) : "unset"}`};
    min-height: ${({ minHeight }) => `${minHeight ? (isNumber(minHeight) ? `${minHeight}px` : minHeight) : "unset"}`};
  }
  .MuiToolbar-dense {
    display: none;
  }
  .MuiTableCell-head {
    background-color: ${background.primary} !important;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    text-align: left;
  }
  .Mui-TableHeadCell-Content-Labels {
    > span {
      margin-left: 4px;
      img {
        opacity: 0.2;
      }
      .MuiBadge-badge {
        display: none;
      }
    }
  }
  .MuiTableCell-body {
    background-color: ${background.white} !important;
    font-size: 14px;
    font-weight: 400;
    line-height: 28px;
  }

  .MuiTableCell-root:has(.MuiCheckbox-root) {
    max-width: 40px;
    min-width: 40px;
  }

  // Default
  .MuiTableRow-root {
    box-shadow: none;
    .MuiTableCell-root {
      padding: ${spacing.xxs} ${spacing.sm};
      border-color: ${divider.middle};
      white-space: nowrap;
      opacity: 1;

      & .MuiCheckbox-root {
        margin-left: 5px;
      }
    }
    &:not(.MuiTableRow-head):last-child {
      .MuiTableCell-root {
        border-bottom: 0;
      }
    }
  }
  .MuiTableHead-root {
    .MuiTableCell-root {
      height: ${({ headRowHeight }) => `${headRowHeight ? (isNumber(headRowHeight) ? `${headRowHeight}px` : headRowHeight) : "unset"}`};
    }
    .Mui-TableHeadCell-Content {
      height: 100%;
    }
  }
  .MuiTableBody-root {
    .MuiTableCell-root {
      height: ${({ bodyRowHeight }) => `${bodyRowHeight ? (isNumber(bodyRowHeight) ? `${bodyRowHeight}px` : bodyRowHeight) : "unset"}`};
    }
  }
  // Hover
  .MuiTableRow-root:not(.MuiTableRow-head):hover {
    &,
    td {
      background-color: ${background.white};
      &::after {
        background-color: ${background.primary};
      }
    }
  }
  // Click/Pressed
  .Mui-selected {
    background-color: ${background.white} !important;
    color: ${button.primary} !important;
    td::after {
      background: ${background.secondary}!important;
    }
  }

  .MuiPaper-root {
    box-shadow: none;
  }
  .MuiPaper-root > .MuiBox-root {
    display: none;
  }

  .MuiCheckbox-root {
    color: ${button.primary};
    height: 24px;
    width: 24px;
    padding: 0;
  }

  .Mui-disabled {
    color: ${button.disable};
  }

  .MuiTableCell-root {
    padding: 8px !important;
    //  background-color: ${background.white} !important;
  }

  // Disabled Row
  .MuiTableCell-root.disabled {
    opacity: 1;
    background: ${background.disable}!important;
    pointer-events: none;
  }

  // added row
  .MuiTableCell-root.added {
    background: ${green[99]}!important;
    &::after {
      background: unset !important;
    }
  }

  // Failed row
  .MuiTableCell-root.failed {
    background: ${background.error}!important;
    &::after {
      background: unset !important;
    }
  }

  // Failed row
  .MuiTableCell-root.clickable {
    cursor: pointer;
  }

  .MuiTypography-root {
    padding: 8px;
  }
`;

const CenterBox = styled(Box)`
  position: absolute;
  margin-top: 17px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  background-color: ${background.white};
  text-align: center;
  padding: 20px;
`;

/* reused button style */
const AddRowButton = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`;
